import { Cell, Merchant, Order } from '@m4a3/api-client';
import { TAGS } from '../constants';
import { createSelector } from 'reselect';
import { RootState } from 'store/configureStore';
import { selectPointOfSales } from './adminSelector';
import { selectClients, selectPads } from './liveAircraftSelectors';
import { getDistanceBetweenPoints } from 'utils/parsingUtils';
import { Swimlanes } from '../constants/orderConstants';

export const getCellName = (state: RootState) => {
  const selectedCellId = state.orders.selectedCellId;
  const currentCell = state.orders.cells?.find(
    cell => cell.id === selectedCellId
  );
  return currentCell ? currentCell.cellName : 'BALBRIGGAN';
};

export const selectJustEatEnabled = (state: RootState) =>state.orders.justEatEnabled;
export const selectCells = (state: RootState): Cell[] => state.orders.cells;
export const selectMerchants = (state: RootState): Merchant[] => state.orders.merchants;
export const selectMerchantsLoading = (state: RootState): boolean => state.orders.merchantsLoading;
export const selectLane = (state: RootState) => state.orders.lane;
export const selectRouteConfirmationOrder = (state: RootState) =>
  state.orders.routeConfirmationOrder;
export const selectFilteredOrders = (state: RootState) =>
  state.orders.ordersToFilter;
export const selectLandingZones = (state: RootState) =>
  state.orders.landingZones;

export const selectSelectedCellId = (state: RootState) =>
  state.orders.selectedCellId;
export const selectOrderDetails = (state: RootState) =>
  state.orders.orderDetails;
export const selectTimeFilter = (state: RootState) => state.orders.timeFilter;
export const selectRefreshInterval = (state: RootState) =>
  state.orders.refreshInterval;
export const selectSelectedOrderId = (state: RootState) =>
  state.orders.selected;
export const selectSelectedOrder = createSelector(
  selectFilteredOrders,
  selectSelectedOrderId,
  (orders, id) => {
    return orders.find(order => order.id === id);
  }
);
export const selectShowCompletedCancelledOrders = (state: RootState) => state.orders.showCompletedCancelledOrders;
export const selectOrderHistoryByIdMap = (state: RootState) => state.orders.orderHistoryById || {};
export const selectIsFetchingOrderHistory = (state: RootState) => state.orders.isFetchingOrderHistory;

// Define completed/cancelled/reset order statuses
const COMPLETED_CANCELLED_STATUSES = [
  'RESET',
  'COMPLETE',
  'CANCELLED',
];

export const selectFilteredOrdersWithCompletedCancelledToggle = createSelector(
  selectFilteredOrders,
  selectShowCompletedCancelledOrders,
  (orders, showCompletedCancelled) => {
    if (showCompletedCancelled) {
      return orders;
    }
    
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    
    return orders.filter(order => {
      // Filter out RESET, COMPLETE, CANCELLED
      if (COMPLETED_CANCELLED_STATUSES.includes(order.status)) {
        return false;
      }
      
      // Filter out REJECTED orders older than 30 minutes
      if (order.status === 'REJECTED' || order.status === 'STORE_REJECTED') {
        const modifyTime = new Date(order.modifyDate).getTime();
        if (modifyTime < thirtyMinutesAgo) {
          return false;
        }
      }
      
      return true;
    });
  }
);

export const selectSortedOrder = createSelector(
  selectFilteredOrdersWithCompletedCancelledToggle,
  selectSelectedOrderId,
  (orders, id) => {
    return orders?.slice().sort((orderA, orderB) => {
      // Priority comparison first (most important)
      const aPriority = orderA?.tags?.includes('PRIORITY');
      const bPriority = orderB?.tags?.includes('PRIORITY');
      if (aPriority && !bPriority) return -1;
      if (bPriority && !aPriority) return 1;
      
      // Then sort by the sort field - SINGLE SORT OPERATION
      return orderA.sort - orderB.sort;
    });
  }
);

export const selectSelectedOrderHistory = createSelector(
  selectSelectedOrderId,
  selectOrderHistoryByIdMap,
  (selectedOrderId, historyMap) => {
    if (!selectedOrderId) {
      return [];
    }
    return historyMap[selectedOrderId] || [];
  }
);

export const selectIsFetchingUpdateAircraft = (state: RootState) =>
  state.orders.isFetchingUpdateAircraft;
export const selectIsFetchingUpdateDeparturePad = (state: RootState) =>
  state.orders.isFetchingUpdateDeparturePad;

export const selectNewOrders = createSelector(
  selectSortedOrder,
  selectTimeFilter,
  orders => {
    return orders.filter(order => order?.swimlane === Swimlanes.NEW_ORDER);
  }
);
export const selectWithOutletOrders = createSelector(
  selectSortedOrder,
  selectTimeFilter,
  orders => {
    return orders.filter(order => order?.swimlane === Swimlanes.WITH_OUTLET);
  }
);
export const selectReadyToGoOrders = createSelector(
  selectSortedOrder,
  selectTimeFilter,
  orders => {
    return orders.filter(order => order?.swimlane === Swimlanes.READY_TO_GO);
  }
);

export const selectActiveOrders = createSelector(
  selectFilteredOrders,
  orders => {
    return orders.filter(
      order =>
        ![
          'COMPLETED',
          'REJECTED',
          'STORE_REJECTED',
          'STORE_UNAVAILABLE',
          'CANCELLED',
          'ABORTED',
          'RESET'
        ].includes(order.status)
    );
  }
);
export const selectToAcceptOrders = createSelector(
  selectWithOutletOrders,
  selectTimeFilter,
  (orders, timeFilter) => {
    return orders
      .filter(
        order =>
          order.status !== 'STORE_PREPARED' &&
          order.status !== 'STORE_PREPARING'
      )
      .sort((a, b) => {
        if (a.status === 'ACCEPTED' && b.status === 'STORE_ACCEPTED') {
          return 1;
        } else if (b.status === 'ACCEPTED' && a.status === 'STORE_ACCEPTED') {
          return -1;
        }
        return 0;
      });
  }
);
export const selectPreparingOrders = createSelector(
  selectWithOutletOrders,
  selectTimeFilter,
  (orders, timeFilter) => {
    return orders.filter(order => order.status === 'STORE_PREPARING');
  }
);
export const selectPreparedOrders = createSelector(
  selectWithOutletOrders,
  selectTimeFilter,
  (orders, timeFilter) => {
    return orders.filter(order => order.status === 'STORE_PREPARED');
  }
);
export const selectReceivedOrders = createSelector(
  selectReadyToGoOrders,
  selectTimeFilter,
  (orders, timeFilter) => {
    return orders.filter(order => order.status === 'RECEIVED_FROM_STORE')
    .sort((a, b) => {
      const timeA = new Date(a.storePreparedDate).getTime();
      const timeB = new Date(b.storePreparedDate).getTime();

      return timeA - timeB; // sorts in ascending order
    });
  }
);

export const selectOrdersWithinTime = createSelector(
  selectSortedOrder,
  selectTimeFilter,
  (orders, timeFilter) => {
    return orders;
  }
);

export const selectComponentOrder = (state: RootState, order: any) => order;
export const selectOverweightColorPairingsForOrder = (state: RootState, orderId: string) => state.orders?.orderIdsWithOverweightColorPairings?.[orderId];

export const selectTeamsAndData = createSelector(
  selectActiveOrders,
  state => state.liveAircraft.clients as Array<{
    type: string;
    team?: string;
    position?: { latitude: number; longitude: number };
    id: string;
  }>,
  selectComponentOrder,
  (orders, clients, order) => {
    return TAGS.filter(tag => tag.includes('TEAM')).map(tag => {
      const teamPos = clients
        ?.filter(client => client.type === 'GroundTeamApp' || client.type === 'CollectorApp')
        .find(client => client?.team === tag)?.position;
      const orderDropLocation = order?.dropLatlng?.split(",");
      let distance = 0;

      if (orderDropLocation) {
        distance = getDistanceBetweenPoints(
          teamPos?.latitude,
          teamPos?.longitude,
          Number(orderDropLocation[0]),
          Number(orderDropLocation[1])
        ) / 1000;
      }

      const orderCount = orders.filter(o => {
        const irrelevantStatusTypesForGroundTeam = [
          'COMPLETED',
          'REJECTED',
          'STORE_REJECTED',
          'STORE_UNAVAILABLE',
          'CANCELLED',
          'ABORTED',
          'RESET'
        ]
        return o?.tags?.includes(tag) && !irrelevantStatusTypesForGroundTeam.includes(o?.status);
      }).length;
      return {
        tag,
        distance,
        orderCount
      };
    });
  }
);
export const selectLanesAndData = createSelector(
  selectActiveOrders,
  state => state.liveAircraft.clients,
  selectComponentOrder,
  (orders, clients, order) => {
    return TAGS.filter(tag => tag.includes('LANE')).map(tag => {
      const orderCount = orders.filter(o => {
        return o?.tags?.includes(tag);
      }).length;
      return {
        tag,
        orderCount
      };
    });
  }
);
export const selectHoverOrder = (state: RootState) => state.orders.hoverOrder;
export const selectHoverTeam = (state: RootState) => state.orders.hoverTeam;
export const selectHoverAircraft = (state: RootState) =>
  state.orders.hoverAircraft;
export const selectHoverDeparturePad = (state: RootState) =>
  state.orders.hoverDeparturePad;
export const selectHoverArrivalPad = (state: RootState) =>
  state.orders.hoverArrivalPad;

export const selectOrder = (state: RootState, order: Order) => order;
export const selectOrderId = (state: RootState, orderId: string) => orderId;
export const selectOrderStatus = (state: RootState, orderStatus: string) =>
  orderStatus;
export const invalidatingAnOrdersDropLocation = (state: RootState) => state.orders.invalidatingAnOrdersDropLocation;

export const selectPointOfSale = createSelector(
  selectPointOfSales,
  selectOrder,
  (pointsOfSale, order) => {
    return pointsOfSale?.find(pos => {
      if (!order?.externalProviderId) return false;
      if (parseInt(order.externalProviderId) === pos?.fdStoreId) {
        return true;
      }
      return false;
    });
  }
);
export const selectIsOrderHovered = createSelector(
  selectHoverOrder,
  selectOrder,
  (hoveredOrder, order) => {
    return hoveredOrder === order.id;
  }
);
export const selectIsOrderSelected = createSelector(
  selectSelectedOrderId,
  selectOrder,
  (selectedOrder, order) => {
    return selectedOrder === order.id;
  }
);

// Memoized pad status selector - calculates pad statuses efficiently
export const selectPadStatusMap = createSelector(
  selectFilteredOrders,
  (orders) => {
    const statusMap = new Map<number, {
      color: string | null;
      label: string | null; 
      count: number;
    }>();
    
    if (!orders || orders.length === 0) return statusMap;
    
    // Group orders by pad efficiently - single pass through orders
    const padOrderMap = new Map<number, Order[]>();
    
    orders.forEach(order => {
      // Check both origin and destination pads
      [order.originLandingPadId, order.destinationLandingPadId].forEach(padId => {
        if (padId) {
          if (!padOrderMap.has(padId)) {
            padOrderMap.set(padId, []);
          }
          padOrderMap.get(padId)!.push(order);
        }
      });
    });
    
    // Calculate status once per pad
    padOrderMap.forEach((padOrders, padId) => {
      const busyStatuses = ['READY', 'DISPATCHED', 'ABORTED'];
      const inactiveStatuses = ['COMPLETE', 'CANCELLED', 'REJECTED', 'STORE_REJECTED', 'RESET', 'DELIVERED'];
      
      const hasBusyOrders = padOrders.some(order => busyStatuses.includes(order.status));
      const hasDeliveredOrders = padOrders.some(order => order.status === 'DELIVERED');
      const activeOrdersCount = padOrders.filter(order => !inactiveStatuses.includes(order.status)).length;
      
      let color: string | null = null;
      let label: string | null = null;
      
      if (hasBusyOrders) {
        color = '#ff9800'; // Orange - pad is busy
        label = 'Busy';
      } else if (hasDeliveredOrders) {
        color = '#2196f3'; // Blue - pad has delivered orders
        label = 'Delivered';
      }
      
      statusMap.set(padId, {
        color,
        label,
        count: activeOrdersCount
      });
    });
    
    return statusMap;
  }
);

// Selector to get status for a specific pad
export const selectPadStatus = createSelector(
  selectPadStatusMap,
  (state: RootState, padId: number) => padId,
  (statusMap, padId) => {
    return statusMap.get(padId) || { color: null, label: null, count: 0 };
  }
);

export const selectLandingZoneId= (_: RootState, landingZoneId: string) => landingZoneId;


export const selectOrderLandingZoneCoords = createSelector(
  selectLandingZoneId,
  selectLandingZones,
  (landingZoneId, landingZones) => {
    const landingZone = landingZones.find(lz => lz.id === landingZoneId);
    return [landingZone?.centreLatitude, landingZone?.centreLongitude] as [number, number];
  }
);

export const selectLandingPadId = (_: RootState, landingPadId: number) => landingPadId;

export const selectOrderOriginLandingPad = createSelector(
  selectLandingPadId,
  selectPads,
  (padId, pads) => {
    const pad = pads.find(p => p.id === padId);
    return pad;
  }
);
export const selectOrderDestinationLandingPad = createSelector(
  selectLandingPadId,
  selectPads,
  (padId, pads) => {
    const pad = pads.find(p => p.id === padId);
    return pad;
  }
);